import {TravelCard} from './TravelCard'
import {DropdownPayload, SearchFilters, Travel, TooltipTextsProps, Termin} from '../types/travel'
import {useState, useRef, useEffect} from "preact/hooks";
import {TerminCard} from "./TerminCard.tsx";
import {Loader} from "./Loader.tsx";
import {GridHeader} from "./GridHeader.tsx";
import {ContentInfusionComponent} from "./ContentInfusionComponent.tsx";

interface ResultsGridProps {
    results: Travel[]
    termine: Termin[]
    loading: boolean
    filters: SearchFilters
    onSortChange: (payload: DropdownPayload) => void
    isFilterOverlayOpen?: boolean
    setIsFilterOverlayOpen?: (isOpen: boolean) => void
    total?: number
    terminTotal?: number
    page?: number
    limit?: number
    contentInfusion?: []
    onLoadMore?: () => void
    onLoadMoreTermine?: () => void
    hasMoreResults?: boolean
    hasMoreTerminResults?: boolean
    loadingMore?: boolean
    currentSortBy?: string
    toolTipTexts?: TooltipTextsProps
    gridViewState: boolean
    setGridViewState: (value: boolean) => void
}


export function ResultsGrid({results, termine, loading, filters, onSortChange, isFilterOverlayOpen, setIsFilterOverlayOpen, total = 0, terminTotal = 0, limit = 11, contentInfusion = [], onLoadMore, onLoadMoreTermine, hasMoreResults = false, hasMoreTerminResults = false, loadingMore = false, currentSortBy, toolTipTexts, gridViewState, setGridViewState}: ResultsGridProps) {

    // Function to get content infusion element for a specific result index
    const getContentInfusionForIndex = (resultIndex: number) => {
        if (!contentInfusion || contentInfusion.length === 0) return null;

        // Calculate which "page" this result index represents
        const pageForIndex = Math.floor(resultIndex / limit) + 1;
        // Get the corresponding content infusion entry (cycling through if needed)
        const contentIndex = (pageForIndex - 1) % contentInfusion.length;
        return contentInfusion[contentIndex] || null;
    };
    const containerRef = useRef<HTMLDivElement>(null);
    const [previousResultsLength, setPreviousResultsLength] = useState(0);

    // Preserve scroll position when new results are loaded
    useEffect(() => {
        if (results.length > previousResultsLength && previousResultsLength > 0) {
            // New results were added (load more case)
            // Use requestAnimationFrame to ensure smooth rendering
            requestAnimationFrame(() => {
                // Maintain current scroll position instead of jumping to top
                const currentScrollY = window.scrollY;
                window.scrollTo(0, currentScrollY);
                setPreviousResultsLength(results.length);
            });
        } else if (results.length <= previousResultsLength) {
            // Results were replaced (filter change or initial load)
            setPreviousResultsLength(results.length);
        }
    }, [results.length, previousResultsLength]);



    if (loading) {
        return <Loader />
    }


    if (results.length === 0) {
        return (
            <div className={`font-reckless`}>
                <h1 className={`text-fluid-mi-18-ma-28 mb-6`}>Keine Reise gefunden</h1>
                <p className={`text-fluid-mi-16-ma-20`}>Bitte wählen Sie einen anderen Termin oder ein anderes Reiseziel.</p>
            </div>
        )
    }

    return (
        <>
            <GridHeader
                filters={filters}
                total={total}
                terminTotal={terminTotal}
                gridViewState={gridViewState}
                setGridViewState={setGridViewState}
                onSortChange={(payload) => onSortChange(payload)}
                isFilterOverlayOpen={isFilterOverlayOpen}
                setIsFilterOverlayOpen={setIsFilterOverlayOpen}
                currentSortBy={currentSortBy}
            />

            <div ref={containerRef}>
                {gridViewState ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-[40px]">
                        {results.map((travel, index) => {
                            // Show content infusion at position 8 within each page (7th index = 8th item)
                            const positionInPage = index % limit;
                            const shouldShowContentInfusion = positionInPage === 7;
                            const contentInfusionElement = shouldShowContentInfusion ? getContentInfusionForIndex(index) : null;
                            return (
                                <>
                                    <TravelCard
                                        key={travel.id}
                                        travel={travel}
                                        toolTipTexts={toolTipTexts}
                                    />
                                    {contentInfusionElement && (
                                        <ContentInfusionComponent data={contentInfusionElement} />
                                    )}
                                </>
                            );
                        })}
                    </div>
                ) : (
                    <div className="grid lg:block grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[30px] lg:pt-0">
                        {termine.map(termin => (
                            <TerminCard
                                key={termin.id}
                                termin={termin}
                                toolTipTexts={toolTipTexts}
                                filters={filters}
                                terminTotal={terminTotal}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Load More Button */}
            {gridViewState && hasMoreResults && onLoadMore && (
                <div className="flex justify-center mt-8">
                    <button
                        onClick={onLoadMore}
                        disabled={loadingMore}
                        className="btn ghost height-60 px-4 py-3 text-lg font-medium">
                        Weitere Reisen
                    </button>
                </div>
            )}

            {!gridViewState && hasMoreTerminResults && onLoadMoreTermine && (
                <div className="flex justify-center mt-8">
                    <button
                        onClick={onLoadMoreTermine}
                        disabled={loadingMore}
                        className="btn ghost height-60 px-4 py-3 text-lg font-medium">
                        Weitere Termine
                    </button>
                </div>
            )}
        </>
    )
}
